import { Stack, Typography, useTheme } from "@mui/material";
import { DekoxIcon } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { IconButton, TooltipShortcut } from "@itk/mui-components";
import { getClickableProps } from "src/utils/keyboard-utils";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { configuratorActions } from "../../configurator-store";
import { TOptionsPanel } from "../../configurator-types";
import { getSortFieldLabel, getConfigurationSortFieldLabel, getGroupFieldLabel, FILTER_WIDTH } from "../../configurator-utils";
import { getTotalFiltersApplied } from "../filter/filter-types";

const mapStateToProps = (state: RootState) => ({
  activeTab: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.activeTab,
  activeOptionsPanel: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.activeOptionsPanel,
  appliedFilter: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter,
  appliedCustomFilters: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedCustomFilters,
  sortField: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.sortField,
  sortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.sortDirection,
  // Configurations tab's own sort state, distinct from Products' sortField/sortDirection above
  configurationSortField: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationSortField,
  configurationSortDirection: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.configurationSortDirection,
  groupFields: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.groupFields
});

const ownActions = {
  setActiveOptionsPanel: configuratorActions.setActiveOptionsPanel
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const OptionsRailBase = (props: Props) => {
  const { activeTab, activeOptionsPanel, appliedFilter, appliedCustomFilters, sortField, sortDirection, configurationSortField, configurationSortDirection, groupFields, setActiveOptionsPanel } =
    props;

  const theme = useTheme();
  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const totalFiltersApplied = getTotalFiltersApplied(appliedFilter, appliedCustomFilters);

  // Keeps the vertical rail label short: show the single field's name, or just a count once
  // there's more than one — concatenating every field name would make the label unreadably long
  const groupByLabel = groupFields.length === 0 ? configuratorPageT.groupByNone : groupFields.length === 1 ? getGroupFieldLabel(configuratorPageT, groupFields[0]) : `(${groupFields.length})`;

  // Resolve once per render which tab's sort field/direction pair is active, so both the label
  // and the "none has no direction" arrow-omission logic below stay symmetric for both tabs
  const currentSortFieldLabel = activeTab === "PRODUCTS" ? getSortFieldLabel(configuratorPageT, sortField) : getConfigurationSortFieldLabel(configuratorPageT, configurationSortField);
  const currentSortField = activeTab === "PRODUCTS" ? sortField : configurationSortField;
  const currentSortDirection = activeTab === "PRODUCTS" ? sortDirection : configurationSortDirection;

  // Accordion: opening a panel closes whichever was open; clicking the already-open one closes it
  const toggle = (panel: NonNullable<TOptionsPanel>) => setActiveOptionsPanel(activeOptionsPanel === panel ? null : panel);

  // Each segment gets its own bordered box (not just stacked text) so the rail reads as three
  // separate clickable buttons rather than one contiguous strip
  const renderSegment = (panel: NonNullable<TOptionsPanel>, shortcutKey: string, label: string) => (
    <Stack
      alignItems="center"
      py={1.5}
      mx={0.5}
      my={0.5}
      {...getClickableProps(() => toggle(panel))}
      onClick={() => toggle(panel)}
      sx={{
        cursor: "pointer",
        outline: "none",
        borderRadius: 1,
        border: `1px solid ${theme.palette.customColors.grey2}`,
        backgroundColor: activeOptionsPanel === panel ? theme.palette.customColors.grey : theme.palette.customColors.lightGrey,
        "&:hover": { backgroundColor: theme.palette.customColors.grey },
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` }
      }}
    >
      <TooltipShortcut shortcut={{ action: () => toggle(panel), shortCutKey: shortcutKey, modifier: "alt" }}>
        <IconButton k={genericTranslationsK.open} size="small" sx={{ color: theme.palette.customColors.darkGrey }}>
          <DekoxIcon icon={activeOptionsPanel === panel ? "doubleArrowRight" : "doubleArrowleft"} fontSize="small" />
        </IconButton>
      </TooltipShortcut>
      <Typography
        variant="body2"
        sx={{
          writingMode: "vertical-rl",
          transform: "rotate(360deg)",
          mt: 0.5,
          color: theme.palette.customColors.darkGrey,
          fontWeight: "normal"
        }}
      >
        {label}
      </Typography>
    </Stack>
  );

  return (
    <Stack
      sx={{
        width: FILTER_WIDTH.CLOSE,
        flexShrink: 0,
        backgroundColor: theme.palette.customColors.white,
        borderLeft: `1px solid ${theme.palette.customColors.grey}`,
        borderTop: `1px solid ${theme.palette.customColors.grey}`,
        alignItems: "center"
      }}
    >
      {renderSegment("viewSettings", "v", configuratorPageT.viewSettings)}
      {/* Filter is Products-only — it narrows that tab's own productsState, and the Configurations
          tab ignores appliedFilter/appliedCustomFilters entirely, so an entry point here would do
          nothing visible while that tab is active. */}
      {activeTab === "PRODUCTS" && renderSegment("filter", "f", `${genericTranslationsT.filter}${totalFiltersApplied > 0 ? ` (${totalFiltersApplied})` : ""}`)}
      {/* Sort options now applies on both tabs — the field/direction shown just picks the right pair per tab.
          Both tabs' "none" field has no direction, so the arrow is omitted whenever the active tab's current field is "none". */}
      {renderSegment("sort", "s", `${configuratorPageT.sortOptions}: ${currentSortFieldLabel}${currentSortField !== "none" ? ` ${currentSortDirection === "asc" ? "▲" : "▼"}` : ""}`)}
      {renderSegment("group", "g", `${configuratorPageT.groupBy}: ${groupByLabel}`)}
    </Stack>
  );
};

export const OptionsRail = connect(mapStateToProps, ownActions)(OptionsRailBase);
